import React from 'react';
import './SettingsPage.scss';
import Title from '../../shared/components/title/Title';
import Tabs from '../../shared/components/tabs/Tabs';
import { TabItem } from '../../shared/components/tabs/models/TabsModel';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { useTranslation } from 'react-i18next';
import { AccountStatus } from '../../shared/types/account/AccountStatus';
import useBilling from '../../shared/hooks/account/UseBilling';
import usePermission from '../../shared/hooks/global/UsePermission';
import BillingIssuesBadge from '../../shared/components/common/billing-issues-badge/BillingIssuesBadge';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';

interface SettingsProps {
  children: React.ReactNode;
  restUrl: string;
}

const SettingsPage: React.FC<SettingsProps> = ({ children, restUrl }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { accountResponse } = useAccount();
  const canShowBilling = useBilling();
  // Bez `read` zakładka ma NIE ISTNIEĆ, a nie być zablokowana — `defaultUserPermissions` odwzorowuje
  // dzisiejsze `isAdminOrOwner`, więc dla kont bez addona nic się nie zmienia.
  const billingAccess = usePermission('BILLING');
  const apiKeysAccess = usePermission('API_KEYS');
  const teamAccess = usePermission('TEAM');
  const unsupportedStatus = ['TRIAL'] as AccountStatus[];
  const menuItems: TabItem[] = [
    {
      icon: 'id_card',
      url: `/settings/my-account`,
      label: t('myAccount'),
      isActive: restUrl === 'my-account',
    },
    {
      icon: 'password',
      url: `/settings/change-password`,
      label: t('changePassword'),
      isActive: restUrl === 'change-password',
    },
    ...(accountResponse &&
    billingAccess.read &&
    accountResponse.customerId &&
    canShowBilling
      ? [
          {
            icon: 'receipt_long',
            url: `/settings/billing-data`,
            label: t('billingData'),
            isActive: restUrl === 'billing-data',
          },
        ]
      : []),
    ...(accountResponse && billingAccess.read && canShowBilling
      ? [
          {
            icon: 'account_balance',
            url: `/settings/subscription`,
            label: t('subscription'),
            isActive: restUrl === 'subscription',
            badge: (
              <BillingIssuesBadge
                targetId="settings-subscription-badge"
                tooltipPosition="bottom"
              />
            ),
          },
        ]
      : []),
    ...(accountResponse &&
    !unsupportedStatus.includes(accountResponse.accountStatus) &&
    accountResponse.customerId &&
    canShowBilling &&
    billingAccess.read
      ? [
          {
            icon: 'shopping_cart',
            url: `/settings/orders`,
            label: t('orders'),
            isActive: restUrl === 'orders',
          },
        ]
      : []),
    ...(teamAccess.read
      ? [
          {
            icon: 'group',
            url: `/settings/team`,
            label: t('team'),
            isActive: restUrl === 'team',
          },
        ]
      : []),
    {
      icon: 'group',
      url: `/settings/your-accounts`,
      label: t('yourAccounts'),
      isActive: restUrl === 'your-accounts',
    },
    ...(accountResponse?.addons?.includes('API_KEYS') && apiKeysAccess.read
      ? [
          {
            icon: 'key',
            url: `/settings/api-keys`,
            label: t('apiKeys'),
            isActive: restUrl === 'api-keys',
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="page-content settings-page">
        <div className="page-box">
          {/* Telefon: zakładki (za dużo na szerokość ekranu) jako dropdown w górnym pasku — w miejscu
              tytułu, bo pokazuje nazwę aktywnej zakładki. */}
          <MobilePageHeader
            title={
              <Dropdown
                value={menuItems.find((item) => item.isActive)?.url}
                onChange={(e) => navigate(e.value)}
                options={menuItems.map((item) => ({ label: item.label, value: item.url }))}
                className="dropdown settings-page__tabs-dropdown"
              />
            }
          />
          {!isMobile && (
            <>
              <Title title={menuItems.filter((item) => item.isActive)[0]?.label ?? ''} />
              <Tabs items={menuItems} additionalClass="page-box__tabs" />
            </>
          )}
          <div className="page-box__content">
            {/* key on the active tab so the content remounts and fades in on every tab switch */}
            <main key={restUrl} className="settings-fade">
              {children}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
